import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Cpu, Zap, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * SpeedIQAnalysisRunner — client-side MediaPipe pose extraction over the
 * uploaded side-view sprint clip. Computes per-frame keypoints and the
 * side-view sprint kinematics defined in SPEEDIQ_BLUEPRINT.md §9.
 *
 * Posts the resulting metrics to `POST /speediq/sessions/{id}/analysis/side`.
 *
 * Reused MediaPipe pipeline pattern from PitchMechanicsCapture (zero new
 * deps). The browser owns the heavy lift; the server just stores the
 * normalized result.
 */
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const WASM_BASE =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

const LM = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
  L_FOOT: 31,     R_FOOT: 32,
};

const deg = (rad) => (rad * 180) / Math.PI;
const angleDeg = (a, b, c) => {
  if (!a || !b || !c) return null;
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const m1 = Math.hypot(v1.x, v1.y); const m2 = Math.hypot(v2.x, v2.y);
  if (!m1 || !m2) return null;
  return deg(Math.acos(Math.max(-1, Math.min(1, dot / (m1 * m2)))));
};

export default function SpeedIQAnalysisRunner({ sessionId, sideAsset, onComplete }) {
  const [phase, setPhase] = useState("idle");        // idle | loading | running | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Reset when the source asset changes.
    setPhase("idle"); setResult(null); setErr(null); setProgress(0);
  }, [sideAsset?.asset_id]);

  async function run() {
    if (!sideAsset) {
      setErr("Side-view clip not attached yet."); return;
    }
    setPhase("loading"); setErr(null); setProgress(0);
    try {
      const vision = await import("@mediapipe/tasks-vision");
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE);
      const landmarker = await vision.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.4,
        minPosePresenceConfidence: 0.4,
        minTrackingConfidence: 0.4,
      });

      setPhase("running");

      // The backend already has the bytes — but we don't currently expose a
      // playable URL for them. Best path: ask the user to re-pick the local
      // file (or re-run on the next session). For P1 we run analysis right
      // after the upload, so the file blob is still in window.__lastSpeedIQClip
      // (set by SpeedIQPage when the user picks). Fall back to the storage
      // metadata if missing.
      const blob = window.__lastSpeedIQClip?.[sideAsset.view] || null;
      if (!blob) {
        throw new Error("Side-view clip blob expired. Re-record to analyze.");
      }
      const url = URL.createObjectURL(blob);
      const v = videoRef.current;
      v.src = url;
      await new Promise((res, rej) => {
        v.onloadeddata = res;
        v.onerror = () => rej(new Error("Could not decode the clip."));
      });
      v.muted = true; v.playsInline = true;

      const fps = 30;
      const dur = v.duration || 6;
      const total = Math.floor(dur * fps);
      const frames = [];

      const cw = (v.videoWidth || 640);
      const ch = (v.videoHeight || 360);
      const c = canvasRef.current;
      c.width = cw; c.height = ch;
      const ctx = c.getContext("2d");

      for (let i = 0; i < total; i++) {
        const t = i / fps;
        v.currentTime = t;
        await new Promise((res) => { v.onseeked = res; });
        ctx.drawImage(v, 0, 0, cw, ch);
        const res = landmarker.detectForVideo(v, performance.now());
        const image = res?.landmarks?.[0];
        const world = res?.worldLandmarks?.[0];
        if (image) frames.push({ t, image, world });
        if (i % 6 === 0) setProgress(Math.round((i / total) * 100));
      }

      URL.revokeObjectURL(url);
      if (frames.length < 6) throw new Error("Not enough frames detected. Re-record with better lighting/framing.");

      const metrics = computeSideViewMetrics(frames, fps);
      setProgress(95);
      setPhase("uploading");
      const { data } = await api.post(`/speediq/sessions/${sessionId}/analysis/side`, metrics);
      setResult({ ...metrics, flags: data.flags || [], flag_count: data.flag_count || 0 });
      setPhase("done");
      setProgress(100);
      if (data.flag_count > 0) {
        toast.warning(`${data.flag_count} side-view metric${data.flag_count === 1 ? "" : "s"} outside the elite window.`);
      } else {
        toast.success("Side-view sprint mechanics analyzed.");
      }
      onComplete?.(data);
    } catch (e) {
      console.error("[speediq] analysis failed", e);
      setErr(e.message || "Analysis failed.");
      setPhase("error");
    }
  }

  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="speediq-analysis-runner">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Side-view pose analysis · P1</div>
          <div className="text-base font-display font-black text-white mt-0.5">
            Extract sprint kinematics from the side-view clip
          </div>
        </div>
        <Cpu className="h-5 w-5 text-white/40" />
      </div>

      {phase === "idle" && (
        <Button onClick={run} className="w-full" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="speediq-run-analysis">
          <Zap className="h-4 w-4 mr-2" /> Run side-view analysis
        </Button>
      )}

      {(phase === "loading" || phase === "running" || phase === "uploading") && (
        <div className="space-y-2" data-testid="speediq-running">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Loader2 className="h-4 w-4 animate-spin" />
            {phase === "loading" ? "Loading pose detector…"
              : phase === "running" ? `Extracting per-frame keypoints… ${progress}%`
              : "Saving analysis to ArmIQ…"}
          </div>
          <div className="w-full h-1.5 bg-black/40 rounded overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${progress}%`, backgroundColor: "var(--brand-secondary)" }} />
          </div>
          <div className="text-[10px] text-white/40">On-device inference · clip never leaves the browser.</div>
        </div>
      )}

      {phase === "done" && result && (
        <div className="space-y-3" data-testid="speediq-analysis-result">
          <div className="rounded p-2 border border-emerald-500/40 bg-emerald-500/5 text-[11px] text-emerald-200 inline-flex items-center gap-1.5">
            <Zap className="h-3 w-3" /> Analysis complete · {result.frame_count} frames · {(result.capture_confidence * 100).toFixed(0)}% capture confidence
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {Object.entries({
              forward_trunk_angle_deg: ["Trunk angle", "°"],
              shin_angle_pushoff_deg: ["Shin @ push-off", "°"],
              first_step_projection_deg: ["1st-step projection", "°"],
              front_side_knee_height_pct: ["Knee height", "%"],
              overstride_index: ["Overstride idx", ""],
              cadence_proxy_hz: ["Cadence", " Hz"],
              vertical_oscillation_proxy_pct: ["Vert oscillation", "%"],
              torso_stability_score: ["Torso stability", ""],
              arm_swing_symmetry_pct: ["Arm symmetry", "%"],
            }).map(([k, [lbl, unit]]) => {
              const v = result[k];
              if (v == null) return null;
              const flagged = (result.flags || []).some((f) => f.metric === k);
              return (
                <div key={k} className="rounded p-2 bg-black/30 border border-white/5" data-testid={`side-metric-${k}`}>
                  <div className="text-[10px] text-white/55 uppercase tracking-wider">{lbl}</div>
                  <div className={`font-display font-bold text-base mt-0.5 ${flagged ? "text-amber-300" : "text-white"}`}>
                    {typeof v === "number" ? v.toFixed(1) : v}{unit}
                  </div>
                </div>
              );
            })}
          </div>
          {result.flags?.length > 0 && (
            <div className="rounded p-3 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-100" data-testid="side-flags">
              <b>{result.flags.length} metric{result.flags.length === 1 ? "" : "s"} outside the elite window.</b> Cause-analysis findings auto-populate when the P4 scoring engine ships next sprint.
            </div>
          )}
          <Button onClick={run} variant="outline" className="w-full border-white/15 text-white/70" data-testid="speediq-rerun">
            Re-run analysis
          </Button>
        </div>
      )}

      {phase === "error" && (
        <div className="rounded p-3 border border-red-500/40 bg-red-500/5 text-xs text-red-200 flex items-start gap-1.5" data-testid="speediq-analysis-error">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-none" />
          <div>
            <b>Analysis failed.</b> {err}
            <Button onClick={run} variant="outline" size="sm" className="mt-2 border-white/20 text-white" data-testid="speediq-retry-analysis">Retry</Button>
          </div>
        </div>
      )}

      <video ref={videoRef} className="hidden" preload="metadata" />
      <canvas ref={canvasRef} className="hidden" />
    </Card>
  );
}

// ─── Side-view sprint metric extractor ────────────────────────────────
function computeSideViewMetrics(frames, fps) {
  let trunkSum = 0, trunkN = 0;
  let kneeHeightMaxPct = 0;
  let shinPushoffMax = 0;
  let firstStepProj = 0;
  let oscMin = Infinity, oscMax = -Infinity;
  let footPlants = [];
  let armSwingL = [], armSwingR = [];
  let headDevSum = 0, headDevN = 0;
  let torsoStability = [];

  const N = frames.length;
  // First-frame anchor (helps detect first-step projection)
  const f0 = frames[0]?.image;
  const startHipY = f0 ? (f0[LM.L_HIP]?.y + f0[LM.R_HIP]?.y) / 2 : 0;

  frames.forEach((f, i) => {
    const lm = f.image;
    if (!lm) return;
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    const la = lm[LM.L_ANKLE], ra = lm[LM.R_ANKLE];
    const nose = lm[LM.NOSE];

    if (ls && rs && lh && rh) {
      const sMid = { x: (ls.x + rs.x) / 2, y: (ls.y + rs.y) / 2 };
      const hMid = { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2 };
      // Trunk angle from vertical (image-space y-down).
      const dx = sMid.x - hMid.x, dy = hMid.y - sMid.y;
      const trunk = Math.abs(deg(Math.atan2(dx, Math.max(0.001, dy))));
      trunkSum += trunk; trunkN++;

      // First-step projection — angle of trunk during first 8 frames
      if (i < 8 && trunk > firstStepProj) firstStepProj = trunk;

      // Vertical oscillation: hip y traces over the clip
      if (hMid.y < oscMin) oscMin = hMid.y;
      if (hMid.y > oscMax) oscMax = hMid.y;

      // Head deviation from shoulder-mid vertical
      if (nose) {
        const headDev = Math.abs(deg(Math.atan2(nose.x - sMid.x, Math.max(0.001, sMid.y - nose.y))));
        headDevSum += headDev; headDevN++;
      }

      // Torso stability — inverse variance of trunk angle within rolling window
      torsoStability.push(trunk);
    }

    // Front-side knee height — peak knee Y rise above hip line, normalized by pelvic height
    if (lh && rh && lk && rk && la && ra) {
      const hipY = (lh.y + rh.y) / 2;
      const ankleY = (la.y + ra.y) / 2;
      const pelvicH = Math.max(0.001, ankleY - hipY);
      const kneeY = Math.min(lk.y, rk.y);
      const kneeRise = Math.max(0, hipY - kneeY);
      const pct = (kneeRise / pelvicH) * 100;
      if (pct > kneeHeightMaxPct) kneeHeightMaxPct = pct;

      // Shin angle at push-off — angle of knee-ankle segment from vertical
      // when ankle is behind hip (back leg push-off proxy).
      [[lk, la, lh], [rk, ra, rh]].forEach(([k, a, h]) => {
        if (a.x < h.x - 0.05) {  // back-leg side (visually behind the hip)
          const shin = Math.abs(deg(Math.atan2(a.x - k.x, Math.max(0.001, a.y - k.y))));
          if (shin > shinPushoffMax) shinPushoffMax = shin;
        }
      });

      // Foot plant detection — local minima of ankle Y (foot at ground)
      footPlants.push({ t: f.t, leftY: la.y, rightY: ra.y });
    }

    // Arm swing — elbow-shoulder angle at wrist max (proxy for amplitude)
    const le = lm[LM.L_ELBOW], re = lm[LM.R_ELBOW];
    const lw = lm[LM.L_WRIST], rw = lm[LM.R_WRIST];
    if (ls && le && lw) {
      const a = angleDeg(lw, le, ls);
      if (a != null) armSwingL.push(180 - a);
    }
    if (rs && re && rw) {
      const a = angleDeg(rw, re, rs);
      if (a != null) armSwingR.push(180 - a);
    }
  });

  // Derived metrics
  const trunkAvg = trunkN ? trunkSum / trunkN : null;
  const oscPct = (oscMin < Infinity && oscMax > -Infinity)
    ? ((oscMax - oscMin) / Math.max(0.001, (oscMax + oscMin) / 2)) * 100 / 4   // dampened
    : null;
  const cadence = estimateCadence(footPlants, fps);
  const headDevAvg = headDevN ? headDevSum / headDevN : null;
  const torsoStdDev = stddev(torsoStability);
  const torsoStability100 = torsoStability.length
    ? Math.max(0, Math.min(100, 100 - torsoStdDev * 4))
    : null;
  const armSymmetry = (armSwingL.length && armSwingR.length)
    ? Math.max(0, 100 - Math.abs(avg(armSwingL) - avg(armSwingR)))
    : null;
  const armAmpAvg = (armSwingL.length || armSwingR.length)
    ? avg([...armSwingL, ...armSwingR])
    : null;

  // Overstride: foot x position past pelvis x at strike — sample mid-clip
  const overstride = sampleOverstride(frames);

  // Approximate stride length from cadence and clip duration. For P1 we
  // surface a coarse proxy; the proper IMU-fused version ships in P4.
  const strideLengthProxy = cadence ? (1.6 + (cadence - 4.0) * 0.3) : null;

  // Capture confidence: mean visibility of shoulders + hips across the clip
  const visScores = [];
  frames.forEach((f) => {
    const lm = f.image;
    if (!lm) return;
    const cs = [LM.L_SHOULDER, LM.R_SHOULDER, LM.L_HIP, LM.R_HIP]
      .map(i => lm[i]?.visibility ?? 0);
    visScores.push(cs.reduce((a, b) => a + b, 0) / cs.length);
  });
  const captureConf = visScores.length ? avg(visScores) : null;

  return {
    forward_trunk_angle_deg: trunkAvg != null ? +trunkAvg.toFixed(1) : null,
    shin_angle_pushoff_deg:  shinPushoffMax > 0 ? +shinPushoffMax.toFixed(1) : null,
    first_step_projection_deg: firstStepProj > 0 ? +firstStepProj.toFixed(1) : null,
    front_side_knee_height_pct: kneeHeightMaxPct > 0 ? +kneeHeightMaxPct.toFixed(1) : null,
    overstride_index: overstride != null ? +overstride.toFixed(2) : null,
    foot_strike_index: null,           // P1.5 — needs IMU or higher-fps capture
    contact_time_proxy_ms: null,       // P1.5
    flight_time_proxy_ms: null,        // P1.5
    cadence_proxy_hz: cadence != null ? +cadence.toFixed(2) : null,
    stride_length_proxy_m: strideLengthProxy != null ? +strideLengthProxy.toFixed(2) : null,
    vertical_oscillation_proxy_pct: oscPct != null ? +Math.max(0, Math.min(20, oscPct)).toFixed(1) : null,
    head_position_dev_deg: headDevAvg != null ? +headDevAvg.toFixed(1) : null,
    torso_stability_score: torsoStability100 != null ? +torsoStability100.toFixed(0) : null,
    arm_swing_amplitude_deg: armAmpAvg != null ? +armAmpAvg.toFixed(1) : null,
    arm_swing_symmetry_pct: armSymmetry != null ? +armSymmetry.toFixed(0) : null,
    elbow_carriage_consistency_pct: null,   // P1.5 — needs longer window
    frame_count: N,
    fps_effective: fps,
    capture_confidence: captureConf != null ? +captureConf.toFixed(2) : null,
    engine_version: "speediq.side.v1",
  };
}

function estimateCadence(plants, fps) {
  if (plants.length < 8) return null;
  // Detect ankle-Y local minima → step count → steps/sec
  const ys = plants.map(p => Math.min(p.leftY, p.rightY));
  let steps = 0;
  for (let i = 2; i < ys.length - 2; i++) {
    if (ys[i] < ys[i - 1] && ys[i] < ys[i + 1] && ys[i] < ys[i - 2] && ys[i] < ys[i + 2]) steps++;
  }
  const durSec = (plants[plants.length - 1].t || (plants.length / fps));
  if (durSec <= 0) return null;
  return Math.max(2, Math.min(6, steps / durSec));
}

function sampleOverstride(frames) {
  if (frames.length < 4) return null;
  // Pick the frame closest to mid-clip and measure foot-x vs hip-x.
  const f = frames[Math.floor(frames.length / 2)];
  const lm = f.image;
  if (!lm) return null;
  const hipX = (lm[LM.L_HIP].x + lm[LM.R_HIP].x) / 2;
  const footAhead = Math.max(lm[LM.L_ANKLE].x, lm[LM.R_ANKLE].x);
  return footAhead - hipX;
}

function avg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
function stddev(arr) {
  if (arr.length < 2) return 0;
  const m = avg(arr);
  return Math.sqrt(avg(arr.map(x => (x - m) ** 2)));
}
